import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';
import {
  colors,
  gradients,
  typography,
  borderRadius,
  getCosmicBackground,
  getCardStyle,
  getButtonStyle,
  getInputStyle,
} from '../theme';

interface User {
  id: number;
  email: string;
  subscription_status: string;
  is_active: boolean;
  role: string;
  created_at: string;
  trial_end_date?: string;
}

const AdminPage: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Create user form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    grant_premium: true,
    notes: ''
  });

  // Handle admin login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      if (!response.ok) {
        throw new Error('Invalid credentials');
      }

      const data = await response.json();
      setToken(data.access_token);
      setIsLoggedIn(true);
      localStorage.setItem('admin_token', data.access_token);
      fetchUsers(data.access_token);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Fetch users
  const fetchUsers = async (authToken?: string) => {
    const useToken = authToken || token;
    if (!useToken) return;

    setLoading(true);
    try {
      let url = `${API_URL}/admin/users/search?page=1&limit=100`;
      if (searchQuery) url += `&query=${encodeURIComponent(searchQuery)}`;
      if (filterStatus) url += `&subscription_status=${filterStatus}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${useToken}` }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied. Admin privileges required.');
        }
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.data?.users || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Create new premium user
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/admin/users/create-premium`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newUser)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create user');
      }

      setSuccess(`User ${newUser.email} created successfully with ${newUser.grant_premium ? 'premium' : 'free'} access!`);
      setNewUser({ email: '', password: '', grant_premium: true, notes: '' });
      setShowCreateForm(false);
      fetchUsers();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Grant premium access
  const handleGrantPremium = async (userId: number, email: string) => {
    if (!window.confirm(`Grant free premium access to ${email}?`)) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/admin/users/${userId}/grant-premium`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to grant premium access');
      }

      setSuccess(`Premium access granted to ${email}!`);
      fetchUsers();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Revoke premium access
  const handleRevokePremium = async (userId: number, email: string) => {
    if (!window.confirm(`Revoke premium access from ${email}? They will be set to free plan.`)) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/admin/users/${userId}/revoke-premium`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to revoke premium access');
      }

      setSuccess(`Premium access revoked from ${email}.`);
      fetchUsers();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check for existing token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsLoggedIn(true);
      fetchUsers(savedToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Logout
  const handleLogout = () => {
    setToken(null);
    setIsLoggedIn(false);
    setUsers([]);
    localStorage.removeItem('admin_token');
  };

  // Login form
  if (!isLoggedIn) {
    return (
      <div style={{
        ...getCosmicBackground(),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
      }}>
        <div style={{
          ...getCardStyle('light'),
          width: '100%',
          maxWidth: '400px',
        }}>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px',
              color: colors.accent.gold,
              textShadow: `0 0 20px rgba(212, 175, 55, 0.5)`,
            }}>★</div>
            <h1 style={{
              fontFamily: typography.fontFamily.heading,
              fontSize: typography.fontSize['2xl'],
              color: colors.background.midnight,
              margin: '0 0 8px 0',
              letterSpacing: typography.letterSpacing.wide,
            }}>
              Admin Portal
            </h1>
            <p style={{ color: colors.text.muted, fontSize: typography.fontSize.sm }}>
              Sign in with your admin credentials
            </p>
          </div>

          {error && (
            <div style={{
              background: colors.status.errorBg,
              color: colors.status.error,
              padding: '12px 16px',
              borderRadius: borderRadius.md,
              marginBottom: '20px',
              fontSize: typography.fontSize.sm,
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                color: colors.primary.celestial,
                marginBottom: '8px',
              }}>
                Email
              </label>
              <input
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                required
                style={getInputStyle('light')}
                placeholder="admin@example.com"
              />
            </div>

            <div style={{ marginBottom: '28px' }}>
              <label style={{
                display: 'block',
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                color: colors.primary.celestial,
                marginBottom: '8px',
              }}>
                Password
              </label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
                style={getInputStyle('light')}
                placeholder="Enter password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                ...getButtonStyle('primary'),
                width: '100%',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <Link to="/" style={{
              color: colors.text.muted,
              fontSize: typography.fontSize.sm,
              textDecoration: 'none',
            }}>
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div style={{
      ...getCosmicBackground(),
      padding: '24px 20px',
      position: 'relative',
    }}>
      {/* Star pattern background */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(2px 2px at 20px 30px, rgba(212, 175, 55, 0.2), transparent),
          radial-gradient(1px 1px at 90px 40px, rgba(255, 255, 255, 0.3), transparent)
        `,
        backgroundSize: '200px 200px',
        pointerEvents: 'none',
        opacity: 0.4,
        zIndex: 0,
      }} />

      <div style={{
        ...getCardStyle('light'),
        maxWidth: '1200px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '28px',
          flexWrap: 'wrap',
          gap: '16px',
        }}>
          <div>
            <h1 style={{
              fontFamily: typography.fontFamily.heading,
              fontSize: typography.fontSize['2xl'],
              color: colors.background.midnight,
              margin: '0 0 6px 0',
              letterSpacing: typography.letterSpacing.wide,
            }}>
              <span style={{ color: colors.accent.gold }}>★</span> Admin Dashboard
            </h1>
            <p style={{ color: colors.text.muted, margin: 0, fontSize: typography.fontSize.sm }}>
              Manage users and premium access
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <button style={{
                ...getButtonStyle('ghost'),
                background: 'rgba(107, 91, 189, 0.1)',
                color: colors.primary.violet,
                border: `1px solid ${colors.primary.lavender}`,
                padding: '10px 16px',
              }}>
                Home
              </button>
            </Link>
            <button onClick={handleLogout} style={{
              background: 'rgba(248, 113, 113, 0.1)',
              color: colors.status.error,
              border: `1px solid ${colors.status.error}`,
              padding: '10px 16px',
              borderRadius: borderRadius.md,
              cursor: 'pointer',
              fontWeight: typography.fontWeight.semibold,
              fontFamily: typography.fontFamily.body,
            }}>
              Logout
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div style={{
            background: colors.status.errorBg,
            color: colors.status.error,
            padding: '14px 18px',
            borderRadius: borderRadius.md,
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            {error}
            <button
              onClick={() => setError('')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                color: colors.status.error,
              }}
            >✕</button>
          </div>
        )}

        {success && (
          <div style={{
            background: colors.status.successBg,
            color: colors.status.success,
            padding: '14px 18px',
            borderRadius: borderRadius.md,
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            {success}
            <button
              onClick={() => setSuccess('')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                color: colors.status.success,
              }}
            >✕</button>
          </div>
        )}

        {/* Actions Bar */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            style={{
              ...getButtonStyle(showCreateForm ? 'ghost' : 'gold'),
              background: showCreateForm ? 'rgba(248, 113, 113, 0.1)' : gradients.goldButton,
              color: showCreateForm ? colors.status.error : colors.background.midnight,
              border: showCreateForm ? `1px solid ${colors.status.error}` : 'none',
            }}
          >
            {showCreateForm ? '\u2715 Cancel' : '+ Create Premium User'}
          </button>

          <input
            type="text"
            placeholder="Search by email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              ...getInputStyle('light'),
              width: '220px',
            }}
          />

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              ...getInputStyle('light'),
              width: '150px',
            }}
          >
            <option value="">All Status</option>
            <option value="premium">Premium</option>
            <option value="free">Free</option>
            <option value="trial">Trial</option>
          </select>

          <button onClick={() => fetchUsers()} style={getButtonStyle('primary')}>
            Search
          </button>
        </div>

        {/* Create User Form */}
        {showCreateForm && (
          <div style={{
            background: 'rgba(74, 63, 138, 0.05)',
            padding: '24px',
            borderRadius: borderRadius.md,
            marginBottom: '24px',
            border: `1px solid ${colors.border.subtle}`,
          }}>
            <h3 style={{
              fontFamily: typography.fontFamily.heading,
              margin: '0 0 20px 0',
              color: colors.background.midnight,
            }}>
              Create New Premium User
            </h3>
            <form onSubmit={handleCreateUser}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.medium,
                    color: colors.primary.celestial,
                    marginBottom: '8px',
                  }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    required
                    style={getInputStyle('light')}
                    placeholder="user@example.com"
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.medium,
                    color: colors.primary.celestial,
                    marginBottom: '8px',
                  }}>
                    Password * (min 8 characters)
                  </label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    required
                    minLength={8}
                    style={getInputStyle('light')}
                    placeholder="Secure password"
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.medium,
                    color: colors.primary.celestial,
                    marginBottom: '8px',
                  }}>
                    Notes (optional)
                  </label>
                  <input
                    type="text"
                    value={newUser.notes}
                    onChange={(e) => setNewUser({ ...newUser, notes: e.target.value })}
                    style={getInputStyle('light')}
                    placeholder="Internal notes about this user"
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    id="grantPremium"
                    checked={newUser.grant_premium}
                    onChange={(e) => setNewUser({ ...newUser, grant_premium: e.target.checked })}
                    style={{ width: '18px', height: '18px', accentColor: colors.accent.gold }}
                  />
                  <label htmlFor="grantPremium" style={{
                    fontSize: typography.fontSize.sm,
                    color: colors.text.muted,
                  }}>
                    Grant Premium Access (free, no payment)
                  </label>
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <button type="submit" disabled={loading} style={getButtonStyle('primary')}>
                  {loading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Users Table */}
        <div style={{ overflowX: 'auto' }}>
          {loading && (
            <p style={{ textAlign: 'center', color: colors.text.muted, padding: '20px' }}>
              Loading...
            </p>
          )}

          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '8px',
          }}>
            <thead>
              <tr>
                {['ID', 'Email', 'Status', 'Role', 'Active', 'Created', 'Actions'].map((header) => (
                  <th key={header} style={{
                    textAlign: 'left',
                    padding: '14px 12px',
                    borderBottom: `2px solid ${colors.border.subtle}`,
                    color: colors.primary.celestial,
                    fontWeight: typography.fontWeight.semibold,
                    fontSize: typography.fontSize.sm,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.wide,
                  }}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{
                    padding: '48px',
                    textAlign: 'center',
                    color: colors.text.muted,
                  }}>
                    No users found. {!searchQuery && 'Click Search to load users.'}
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td style={{
                      padding: '14px 12px',
                      borderBottom: `1px solid rgba(107, 91, 189, 0.1)`,
                      color: colors.text.muted,
                    }}>{user.id}</td>
                    <td style={{
                      padding: '14px 12px',
                      borderBottom: `1px solid rgba(107, 91, 189, 0.1)`,
                      color: colors.background.midnight,
                      fontWeight: typography.fontWeight.medium,
                    }}>{user.email}</td>
                    <td style={{
                      padding: '14px 12px',
                      borderBottom: `1px solid rgba(107, 91, 189, 0.1)`,
                    }}>
                      <span style={{
                        padding: '5px 14px',
                        borderRadius: borderRadius.full,
                        fontSize: typography.fontSize.xs,
                        fontWeight: typography.fontWeight.semibold,
                        textTransform: 'uppercase',
                        background: user.subscription_status === 'premium' ? colors.status.successBg :
                                   user.subscription_status === 'trial' ? colors.status.warningBg :
                                   'rgba(107, 91, 189, 0.1)',
                        color: user.subscription_status === 'premium' ? colors.status.success :
                               user.subscription_status === 'trial' ? colors.status.warning :
                               colors.text.muted,
                      }}>
                        {user.subscription_status}
                      </span>
                    </td>
                    <td style={{
                      padding: '14px 12px',
                      borderBottom: `1px solid rgba(107, 91, 189, 0.1)`,
                      color: colors.text.muted,
                      textTransform: 'capitalize',
                    }}>{user.role}</td>
                    <td style={{
                      padding: '14px 12px',
                      borderBottom: `1px solid rgba(107, 91, 189, 0.1)`,
                      color: user.is_active ? colors.status.success : colors.status.error,
                    }}>
                      {user.is_active ? '\u2713 Yes' : '\u2715 No'}
                    </td>
                    <td style={{
                      padding: '14px 12px',
                      borderBottom: `1px solid rgba(107, 91, 189, 0.1)`,
                      color: colors.text.muted,
                      fontSize: typography.fontSize.sm,
                    }}>
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td style={{
                      padding: '14px 12px',
                      borderBottom: `1px solid rgba(107, 91, 189, 0.1)`,
                    }}>
                      {user.subscription_status !== 'premium' ? (
                        <button
                          onClick={() => handleGrantPremium(user.id, user.email)}
                          style={{
                            background: colors.status.successBg,
                            color: colors.status.success,
                            border: `1px solid ${colors.status.success}`,
                            padding: '6px 14px',
                            borderRadius: borderRadius.md,
                            fontSize: typography.fontSize.xs,
                            fontWeight: typography.fontWeight.semibold,
                            cursor: 'pointer',
                          }}
                        >
                          Grant Premium
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRevokePremium(user.id, user.email)}
                          style={{
                            background: colors.status.errorBg,
                            color: colors.status.error,
                            border: `1px solid ${colors.status.error}`,
                            padding: '6px 14px',
                            borderRadius: borderRadius.md,
                            fontSize: typography.fontSize.xs,
                            fontWeight: typography.fontWeight.semibold,
                            cursor: 'pointer',
                          }}
                        >
                          Revoke Premium
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '28px',
          textAlign: 'center',
          color: colors.text.muted,
          fontSize: typography.fontSize.xs,
        }}>
          StarDecision Admin Panel • Total Users: {users.length}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
